import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { formatSeconds, calculateDistance } from '../utils';
import { useStoreState, useStoreActions } from '../store';
import { VHSEventType, VHSEventDetails, PlayerStatus } from '../typings';
import { theme } from '../utils/theme';

export const TimelineControl = () => {
  const playbackDurationTime = useStoreState(state => state.player.playbackDurationTime);
  const playbackCurrentTime = useStoreState(state => state.player.playbackCurrentTime);
  const castSession = useStoreState(state => state.chromecast.castSession);
  const status = useStoreState(state => state.player.status);
  const actions = useStoreActions(actions => actions);
  const containerElementRef = useRef<HTMLDivElement | null>(null);
  const tooltipElementRef = useRef<HTMLDivElement | null>(null);
  const thumbnailElementRef = useRef<HTMLDivElement | null>(null);
  const progressElementRef = useRef<HTMLDivElement | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  function handleMouseEnter() {
    setTooltipVisible(true);
  }

  function handleMouseLeave() {
    setTooltipVisible(false);
  }

  function handleMouseDown(e: MouseEvent | TouchEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    handleWindowMouseMove(e as MouseEvent);
    document.addEventListener('mousemove', handleWindowMouseMove);
    document.addEventListener('touchmove', handleWindowMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchend', handleMouseUp);
  }

  function handleMouseUp(e: MouseEvent | TouchEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    document.removeEventListener('mousemove', handleWindowMouseMove);
    document.removeEventListener('touchmove', handleWindowMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.removeEventListener('touchend', handleMouseUp);

    const offset = calculateDistance(e, containerElementRef.current);
    actions.player.seek((offset / 100) * playbackDurationTime);

    if (!castSession) {
      switch (status) {
        case PlayerStatus.Playing:
          actions.vhs.sendVHSEvent({
            eventType: VHSEventType.play,
            eventDetails: VHSEventDetails.user_generated,
          });
          break;
        case PlayerStatus.Paused:
          actions.vhs.sendVHSEvent({
            eventType: VHSEventType.pause,
            eventDetails: VHSEventDetails.user_generated,
          });
          break;
      }
    }
  }

  function handleMouseMove(e: MouseEvent | TouchEvent) {
    const offset = calculateDistance(e, containerElementRef.current);
    updateTooltipPosition(offset);
  }

  function handleWindowMouseMove(e: MouseEvent | TouchEvent) {
    const offset = calculateDistance(e, containerElementRef.current);
    updateProgress(offset);
    updateThumbnailPosition(offset);
    updateTooltipPosition(offset);
  }

  function updateTooltipPosition(offset: number) {
    const { current: tooltipElement } = tooltipElementRef;
    if (!tooltipElement) return;
    if (offset > 100) offset = 100;
    tooltipElement.innerHTML = formatSeconds(offset * playbackDurationTime * 0.01);
    tooltipElement.style.marginLeft = `${(tooltipElement.clientWidth / 2) * -1}px`;
    tooltipElement.style.left = `${offset}%`;
  }

  function updateThumbnailPosition(offset: number) {
    const { current: thumbnailElement } = thumbnailElementRef;
    if (!thumbnailElement) return;
    if (offset > 100) offset = 100;
    thumbnailElement.style.left = `${offset}%`;
  }

  function updateProgress(offset: number) {
    const { current: progressElement } = progressElementRef;
    if (!progressElement) return;
    if (offset > 100) offset = 100;
    progressElement.style.width = `${offset}%`;
  }

  /**
   * Update timeline progress from application state,
   * unless the user is dragging the timeline control.
   */
  React.useEffect(() => {
    if (isDragging) return;

    let offset = (playbackCurrentTime / playbackDurationTime) * 100;

    updateThumbnailPosition(offset);
    updateProgress(offset);
  }, [isDragging, playbackCurrentTime, playbackDurationTime]);

  return (
    <Container
      data-testid="timeline"
      className="timeline"
      ref={containerElementRef}
      tooltipVisible={tooltipVisible || isDragging}
      onMouseEnter={() => handleMouseEnter()}
      onMouseLeave={() => handleMouseLeave()}
      onMouseDown={e => handleMouseDown(e.nativeEvent)}
      onMouseMove={e => handleMouseMove(e.nativeEvent)}
      onTouchStart={e => handleMouseDown(e.nativeEvent)}
      onTouchMove={e => handleMouseMove(e.nativeEvent)}
    >
      <div className="timeline-controls">
        <div className="track-bar" />
        <div className="progress-bar" ref={progressElementRef} />
        <div className="thumb" ref={thumbnailElementRef} />
        <div className="tooltip" data-testid="tooltip" ref={tooltipElementRef} />
      </div>
    </Container>
  );
};

const Container = styled.div<{ tooltipVisible: boolean }>`
  width: 100%;
  height: 5px;
  background-color: ${theme.lighterGray};
  box-sizing: border-box;
  position: relative;
  cursor: 'pointer';

  .timeline-controls {
    width: 100%;
    height: 100%;
    position: relative;

    .track-bar {
      width: 100%;
      height: 100%;
      position: absolute;
      border: none;
    }

    .progress-bar {
      width: 0%;
      height: 100%;
      position: absolute;
      border: none;
      background: ${theme.primaryBlue};
    }

    .thumb {
      position: absolute;
      left: 0%;
      width: 24px;
      height: 24px;
      border-radius: 12px;
      background-color: ${({ theme }) => theme.primaryBlue};
      margin: -10px 0 0 -12px;

      @media ${theme.mobileMediaQuery} {
        width: 16px;
        height: 16px;
        border-radius: 8px;
        margin: -6px 0 0 -8px;
      }
    }

    .tooltip {
      color: #ffffff;
      text-decoration: none;
      font-size: 11px;
      letter-spacing: 1px;
      font-family: Lato-Light;
      display: ${p => (p.tooltipVisible ? 'block' : 'none')};
      position: absolute;
      height: 18px;
      background-color: black;
      border: 1px solid darkgray;
      border-radius: 9px;
      top: -30px;

      padding: 2px 6px;
      box-sizing: border-box;
    }
  }
`;
